import _ from 'lodash'
import dayjs, { Dayjs } from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import ArrowBackIosNewTwoToneIcon from '@mui/icons-material/ArrowBackIosNewTwoTone'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded'
import HorizontalRuleRoundedIcon from '@mui/icons-material/HorizontalRuleRounded'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'

import { adapterLocaleMap } from '@/utils/locale'

dayjs.extend(isoWeek)

export type AvailabilitiesProps = {
  availabilities: Array<{
    date: string
    slots: Array<{ start: string; end: string }>
  }>
  defaultShowAllSlots?: boolean
  maxAvailabilitiesDay?: number
  minSlotsPerDayShown?: number
  onNext?: (dateFrom: Date, dateTo: Date) => void
  onPrevious?: (dateFrom: Date, dateTo: Date) => void
  onSelectSlot?: (start: Date, end: Date) => void
  selectedSlot?: string
  startFrom?: Date
}

export function Availabilities(props: AvailabilitiesProps) {
  const {
    availabilities,
    defaultShowAllSlots = false,
    maxAvailabilitiesDay = 7,
    minSlotsPerDayShown = 4,
    onNext,
    onPrevious,
    onSelectSlot,
    selectedSlot,
    startFrom,
  } = props

  const [showAllSlots, setShowAllSlots] = useState<boolean>(defaultShowAllSlots)
  const { i18n } = useTranslation()

  useEffect(() => {
    dayjs.locale(i18n.language)
  }, [i18n.language])

  const [weekStartFrom, setWeekStartFrom] = useState<Dayjs>(startFrom ? dayjs(startFrom) : dayjs().startOf('isoWeek'))
  const availabilitesDays = useMemo(() => {
    return _.range(maxAvailabilitiesDay).map((number) => dayjs(weekStartFrom).add(number, 'day'))
  }, [maxAvailabilitiesDay, weekStartFrom])

  const maxSlotsPerDayShown = useMemo(() => {
    let max = minSlotsPerDayShown

    _.forEach(availabilities, ({ slots }) => {
      max = Math.max(max, slots.length)
    })

    return max
  }, [availabilities, minSlotsPerDayShown])

  const slotsByDate = useMemo(() => {
    return _.reduce(
      availabilities,
      (result: Record<string, Array<{ start: string; end: string }>>, availability) => {
        if (!result[availability.date]) {
          result[availability.date] = availability.slots
        }
        return result
      },
      {}
    )
  }, [availabilities])

  const handleNext = () => {
    const newWeekStartFrom = dayjs(weekStartFrom).add(maxAvailabilitiesDay, 'day')
    setWeekStartFrom(newWeekStartFrom)
    onNext?.(
      newWeekStartFrom.toDate(),
      newWeekStartFrom
        .clone()
        .add(maxAvailabilitiesDay - 1, 'day')
        .toDate()
    )
  }

  const handlePrevious = () => {
    const newWeekStartFrom = dayjs(weekStartFrom).subtract(maxAvailabilitiesDay, 'day')
    setWeekStartFrom(dayjs(weekStartFrom).subtract(maxAvailabilitiesDay, 'day'))
    onPrevious?.(
      newWeekStartFrom.toDate(),
      newWeekStartFrom
        .clone()
        .add(maxAvailabilitiesDay - 1, 'day')
        .toDate()
    )
  }

  const toggleShowAllSlots = () => {
    setShowAllSlots(!showAllSlots)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={adapterLocaleMap[i18n.language.split('-')[0]]}>
      <Stack spacing={2} justifyContent="center">
        <Stack spacing={1} direction="row" alignItems="start" justifyContent="space-between">
          <IconButton id="previous-button" data-testid="previous-button" onClick={handlePrevious} size="small" sx={{ mt: '10px !important' }}>
            <ArrowBackIosNewTwoToneIcon color="primary" fontSize="small" />
          </IconButton>
          <Box sx={{ overflowX: 'auto' }}>
            <Stack width="max-content" spacing={1} direction="row" alignItems="start" justifyContent="center" pb={2}>
              {availabilitesDays.map((availabilitesDay: Dayjs) => {
                const date = dayjs(availabilitesDay).format('DD/MM/YYYY')
                if (!slotsByDate[date]) return null
                const slots = !!slotsByDate[date].length
                  ? _.take(slotsByDate[date], showAllSlots ? maxSlotsPerDayShown : minSlotsPerDayShown)
                  : _.fill(Array(showAllSlots ? maxSlotsPerDayShown : minSlotsPerDayShown), { start: '-', end: '-' })
                return (
                  <Stack key={availabilitesDay.valueOf()} sx={{ minWidth: 60 }} spacing={1} direction="column" justifyContent="flex-start" alignItems="center">
                    <Typography sx={{ fontSize: '14px', fontWeight: 700 }}>{_.capitalize(availabilitesDay.format('ddd'))}</Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>{availabilitesDay.format('DD MMM')}</Typography>
                    {_.map(slots, (slot, index) => {
                      return slot.start === '-' ? (
                        <Stack key={`${date} - ${index}`} alignItems="center" justifyContent="center" sx={{ height: 35 }}>
                          <HorizontalRuleRoundedIcon fontSize="small" htmlColor="#2b466033" />
                        </Stack>
                      ) : (
                        <Chip
                          sx={[
                            { height: 35, backgroundColor: '#00DFC4' },
                            selectedSlot === slot.start && {
                              backgroundColor: 'primary.main',
                              color: 'white',
                            },
                          ]}
                          id={`${availabilitesDay.get('day')}-${index}`}
                          data-testid={`${availabilitesDay.get('day')}-${index}`}
                          key={`${slot} - ${index}`}
                          label={dayjs(slot.start).isValid() ? dayjs(slot.start).format('HH[h]mm') : ''}
                          onClick={() => {
                            onSelectSlot?.(dayjs(slot.start).toDate(), dayjs(slot.end).toDate())
                          }}
                        />
                      )
                    })}
                  </Stack>
                )
              })}
            </Stack>
          </Box>
          <IconButton id="next-button" data-testid="next-button" onClick={handleNext} size="small" sx={{ mt: '10px !important' }}>
            <ArrowForwardIosIcon color="primary" fontSize="small" />
          </IconButton>
        </Stack>

        {maxSlotsPerDayShown > minSlotsPerDayShown && (
          <Stack direction="row" justifyContent="center" alignItems="center">
            <Button
              endIcon={!showAllSlots ? <KeyboardArrowDownRoundedIcon /> : <KeyboardArrowUpRoundedIcon />}
              onClick={toggleShowAllSlots}
              size="small"
              variant="contained"
            >
              {showAllSlots ? 'Show less' : 'Show more'}
            </Button>
          </Stack>
        )}
      </Stack>
    </LocalizationProvider>
  )
}

export default Availabilities
